import { Component, ViewChild, Input } from '@angular/core';
import { Tender, Customers, LoadTypes, Orders } from '../../interfaces';
import { NewOrderDialogComponent } from '../new-order-dialog/new-order-dialog.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DataServiceService } from '../../services/data-service.service';
import { MessageService } from 'primeng/api';


@Component({
  selector: 'app-tender-dialog',
  templateUrl: './tender-dialog.component.html',
  styleUrl: './tender-dialog.component.scss'
})
export class TenderDialogComponent {
  constructor(private sanitizer: DomSanitizer, private dataServiceService: DataServiceService,
    private messageService: MessageService) { }

  @ViewChild(NewOrderDialogComponent) newOrderDialog!: NewOrderDialogComponent;

  @Input() customers: Customers[] = [];
  @Input() receiverOptions: any[] = [];
  @Input() supplierOptions: any[] = [];
  @Input() trailers: any[] = [];
  @Input() loadTypesArray: LoadTypes[] = [];
  @Input() loadType: any[] = [];
  @Input() allOrders: Orders[] = [];

  visible: boolean = false;
  tenders: Tender[] = [];
  newTenders: Tender[] = [];
  cancelledTenders: Tender[] = [];
  selectedTender: Tender | null = null;
  emailContent: SafeHtml | null = null;
  emailSubject: string = '';
  emailFrom: string = '';
  loading: boolean = false;
  tenderVisible: boolean = false;
  activeTabIndex: number = 0;
  showPullOutAnimation: boolean = false;
  tenderCounter: number = 0;
  tenderTotal: number = 0;


  showDialog() {
    this.visible = true;
    this.loading = true;
    this.emailContent = null;
    this.emailSubject = 'Loading Tenders...';
    this.emailFrom = '';
  }


  loadTenderContent(tenders: any[] | any) {
    if (!tenders) {
      console.error('No tender data received');
      this.handleError('No tender data received');
      return;
    }
    console.log('Received tender:', tenders[0].extracted_info.order_number);

    try {
      // If we received a single tender, wrap it in an array
      const tendersArray = Array.isArray(tenders) ? tenders : [tenders];

      // Use the first tender in the array
      const tender = tendersArray[0];

      if (!tender?.htmlContent) {
        throw new Error('Tender HTML content is missing');
      }

      this.emailContent = this.sanitizer.bypassSecurityTrustHtml(tender.htmlContent);
      this.emailSubject = tender.subject || 'Tender Details';
      this.emailFrom = tender.fromName || tender.from || 'Unknown Sender';
      this.loading = false;
      this.tenders = tendersArray;
      this.separateTenders();
    } catch (error) {
      console.error('Error processing tender:', error);
      this.handleError('Error loading tender content');
    }
  }


  loadTenderDisplay(tender: Tender) {
    this.selectedTender = tender;

    if (!tender) {
      console.error('No tender data received');
      this.handleError('No tender data received');
      return;
    }

    try {
      if (!tender?.htmlContent) {
        throw new Error('Tender HTML content is missing');
      }

      this.emailContent = this.sanitizer.bypassSecurityTrustHtml(tender.htmlContent);
      this.emailSubject = tender.subject || 'Tender Details';
      this.emailFrom = tender.fromName || tender.from || 'Unknown Sender';
      this.loading = false;
      this.tenderVisible = true;
    } catch (error) {
      console.error('Error processing tender:', error);
      this.handleError('Error loading tender content');
    }
  }


  handleError(message: string) {
    this.emailContent = this.sanitizer.bypassSecurityTrustHtml(`<p>${message}</p>`);
    this.emailSubject = 'Tender Details';
    this.emailFrom = 'Unknown Sender';
    this.loading = false;
  }

  openTenderDialog(tenders: any[] | any) {
    this.showDialog();
    this.loadTenderContent(tenders);
  }


  openNewOrder(tender: Tender) {
    if (this.newOrderDialog) {
      // Let the NewOrderDialog handle the tender data
      this.newOrderDialog.handleTender(tender);
      const dialogSub = this.newOrderDialog.orderCreated.subscribe(() => {
        // Remove the tender from the array
        if (this.activeTabIndex === 0) {
          this.newTenders = this.newTenders.filter(t => t.messageId !== tender.messageId);
        }
        else {
          this.cancelledTenders = this.cancelledTenders.filter(t => t.messageId !== tender.messageId);
        }
        dialogSub.unsubscribe();
        this.tenderVisible = false;
      });
    }
  }

  handleTenderDialogHide() {
    this.tenderVisible = false;
    this.selectedTender = null;  // Reset the selected tender
    this.emailContent = null;    // Clear the email content
    this.tenderCounter = 0;      // Reset the counter
    this.tenderTotal = 0;        // Reset the total
  }

  getDeliveryLocation(location: string): string {
    if (location) {
      if (location.includes('-')) {
        return location.split('-')[0].trim();
      }
      else if (location.includes(';')) {
        return location.split(';')[0].trim();
      }
      else {
        return location;
      }
    }
    else {
      return '';
    }
  }


  updateOrderStatus(tender: Tender) {
    const order = this.allOrders.find((order) => order.pickup_number === tender.extracted_info.order_number);
    if (order) {
      this.dataServiceService.updateOrder(order.order_id, {
        load_status: 'Cancelled',
        is_tender: true,
        message_id: tender.messageId,
        cancel_html: tender.htmlContent
      }).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Order status updated'
          });
        },
        error: (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update order status'
          });
        }
      });
      this.tenderVisible = false;
    }
    else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Order not found'
      });
    }
  }

  private separateTenders() {
    this.newTenders = this.tenders.filter(tender => !tender.is_cancelled);
    this.cancelledTenders = this.tenders.filter(tender => tender.is_cancelled);
  }

  async addAllTenders() {
    this.handleTenderQueue('new');
  }

  updateAllTenders() {
    this.handleTenderQueue('cancelled');
  }

  private handleTenderQueue(type: 'new' | 'cancelled') {
    // Create a queue of tenders to process based on type
    const tenderQueue = type === 'new' ? [...this.newTenders] : [...this.cancelledTenders];
    this.tenderTotal = tenderQueue.length;
    this.tenderCounter = 0;  // Reset counter at start
    
    if (tenderQueue.length === 0) {
      return;
    }

    this.loadTenderDisplay(tenderQueue[0]);

    const processTender = () => {
      if (tenderQueue.length === 0) {
        this.tenderCounter = 0;
        this.visible = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Complete',
          detail: 'All tenders have been processed'
        });
        return;
      }

      const currentTender = tenderQueue.shift();
      if (!currentTender) return;

      this.tenderCounter += 1;

      // Handle tender based on type
      if (type === 'new') {
        this.openNewOrder(currentTender);
        const dialogSub = this.newOrderDialog.orderCreated.subscribe(() => {
          dialogSub.unsubscribe();
          this.handleNextTender(tenderQueue, processTender);
        });
      } else {
        this.updateOrderStatus(currentTender);
        this.handleNextTender(tenderQueue, processTender);
      }
    };

    // Start processing the first tender
    processTender();
  }

  private handleNextTender(tenderQueue: Tender[], processTender: () => void) {
    this.showPullOutAnimation = false;
    this.tenderVisible = false;

    if (tenderQueue[0]) {
      this.loadTenderDisplay(tenderQueue[0]);
      this.tenderVisible = true;
      this.showPullOutAnimation = true;

      // Reset animation flag after it completes
      setTimeout(() => {
        this.showPullOutAnimation = false;
        processTender();
      }, 300);
    } else {
      processTender();
    }
  }
}

